import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

/**
 * Ref-counted Supabase Realtime channel registry.
 *
 * Several hooks (notifications, change-requests, members, …) are mounted in
 * multiple places at once — header, layout and page all subscribe to the same
 * table. Previously each mount opened its own channel, so a single tenant ran
 * 3+ duplicate websocket subscriptions per table, and because cleanup used a
 * fire-and-forget `removeChannel`, orphaned channels accumulated across
 * navigation and the app got progressively slower.
 *
 * This registry guarantees exactly ONE channel per topic, fans the payload out
 * to every subscriber, and removes the underlying channel only when the last
 * subscriber detaches. Topic names already encode their filter (e.g.
 * `members-realtime-${tenantId}`), so all subscribers to a topic share an
 * identical `postgres_changes` binding.
 */

export interface TableChangePayload {
	eventType: 'INSERT' | 'UPDATE' | 'DELETE';
	new: Record<string, unknown>;
	old: Record<string, unknown>;
}

interface PostgresChangesFilter {
	event: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
	schema: string;
	table: string;
	filter?: string;
}

type Handler = (payload: TableChangePayload) => void;

interface Entry {
	channel: RealtimeChannel;
	handlers: Set<Handler>;
}

const registry = new Map<string, Entry>();

// Read-only diagnostics for verifying the app doesn't accumulate channels over a
// session. `window.__realtimeChannels()` returns the live channel count on the
// shared client; `__realtimeTopics()` lists the registry's active topics.
if (typeof window !== 'undefined') {
	const w = window as unknown as {
		__realtimeChannels?: () => number;
		__realtimeTopics?: () => string[];
	};
	w.__realtimeChannels = () => supabase.getChannels().length;
	w.__realtimeTopics = () => [...registry.keys()];
}

/**
 * Subscribe to a Postgres-changes stream for `topic`. Returns an unsubscribe
 * function; the channel is torn down once every subscriber has unsubscribed.
 */
export function subscribeToTable(
	topic: string,
	filter: PostgresChangesFilter,
	handler: Handler,
): () => void {
	let entry = registry.get(topic);

	if (!entry) {
		const handlers = new Set<Handler>();
		const channel = supabase
			.channel(topic)
			// supabase's typed `.on` overload doesn't expose this filter shape generically,
			// so the runtime-correct filter object is passed through a loose cast.
			// biome-ignore lint/suspicious/noExplicitAny: see comment above
			.on('postgres_changes', filter as any, (payload: TableChangePayload) => {
				for (const h of handlers) h(payload);
			})
			.subscribe((_status, err) => {
				if (err) console.warn(`[realtime:${topic}]`, err.message);
			});
		entry = { channel, handlers };
		registry.set(topic, entry);
	}

	entry.handlers.add(handler);

	return () => {
		const current = registry.get(topic);
		if (!current) return;
		current.handlers.delete(handler);
		if (current.handlers.size === 0) {
			supabase.removeChannel(current.channel);
			registry.delete(topic);
		}
	};
}
